import { useAuth0 } from '@auth0/auth0-react'

import React, { useEffect, useState } from 'react'
import { useHistory, useLocation, useParams } from 'react-router-dom'

import { AuthPromptModal } from '../components/AuthPromptModal'
import {
  ReviewGetStartedAssurances,
  ReviewGetStartedDisclaimer,
  ReviewGetStartedIntro,
} from '../components/ReviewGetStartedTrust'
import { ReviewProgressBar } from '../components/ReviewProgressBar'
import { ReviewRatingField } from '../components/ReviewRatingField'
import { ReviewStepHeader } from '../components/ReviewStepHeader'
import { ReviewWrapUpIntro } from '../components/ReviewWrapUpIntro'
import { ReviewYesNoField } from '../components/ReviewYesNoField'
import { Spinner } from '../components/Spinner'
import { TopLogo } from '../components/TopLogo'
import { FormServiceAddSubmissionAnswerEvent } from '../events/FormServiceAddSubmissionAnswerEvent'
import { useAppToast } from '../hooks/useAppToast'
import { FormFieldDTO } from '../models/FormFieldDTO.model'
import { FormFieldTypes } from '../models/FormFieldTypes.model'
import { FormPageDTO } from '../models/FormPageDTO.model'
import { FormRelations } from '../models/FormRelations.model'
import { FormSubmissionDTO } from '../models/FormSubmissionDTO.model'
import { FormSubmissionError } from '../models/FormSubmissionError.model'
import { UserDTO } from '../models/UserDTO.model'
import { AgencyService } from '../services/AgencyService/AgencyService'
import { FormService } from '../services/FormService/FormService'
import { HospitalService } from '../services/HospitalService/HospitalService'
import UserService from '../services/UserService/UserService'
import { scrollIntoView } from '../utils'
import { useCanPerformVerifiedAction, useShouldDeferWriteGate } from '../utils/emailVerification'
import {
  isGetStartedPageComplete,
  isReviewQuestionPageComplete,
  isWouldWorkAgainField,
  isWrapUpVisibleField,
  LAST_WORKED_MONTH_INPUT_NAME,
} from '../utils/reviewFormNavigation'
import {
  formIdToReviewType,
  getEntityDetailPathFromSubmission,
  ReviewEntityType,
  waitForReviewSuccessNavDelay,
} from '../utils/reviewSuccessNavigation'

// Reuse components from Review.tsx
type FieldValues = Record<string | number, unknown>
type UpdateFieldValueMap = (fieldID: string | number, value: unknown) => void
type ValidationErrors = Record<string, string | JSX.Element>

type FormFieldElement = {
  field: FormFieldDTO
  updateFieldValueMap: UpdateFieldValueMap
  validationErrors: ValidationErrors
  fieldValue?: unknown
}

const SectionListItem = ({ field, compact }: { field: FormFieldDTO; compact?: boolean }) => {
  if (compact) {
    return (
      <div className="mb-2 text-center">
        <h2 className="text-lg font-bold text-graphite-grey md:text-xl">{field.label}</h2>
      </div>
    )
  }

  return (
    <div className="mb-8 px-4">
      <h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl text-center font-bold text-graphite-grey break-words">
        {field.label}
      </h2>
    </div>
  )
}

const TextAreaListItem = ({
  field,
  updateFieldValueMap,
  validationErrors,
  fieldValue,
  isWrapUp,
}: FormFieldElement & { isWrapUp?: boolean }) => {
  const placeholder = isWrapUp
    ? "Add context, examples, or anything the ratings didn't capture."
    : !field.isRequired
      ? 'Write your optional text here...'
      : ''

  return (
    <div id={`review-input-${field.id}`} className="mb-6">
      {!isWrapUp && (
        <label className="block text-sm font-semibold text-graphite-grey mb-2">
          {field.label}
          {!field.isRequired && (
            <span className="ml-2 text-xs font-normal text-gray-500 bg-blue-100 text-transform: uppercase text px-2 py-1 rounded-2xl">
              Optional
            </span>
          )}
        </label>
      )}
      <textarea
        className={`w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white resize-none ${
          isWrapUp ? 'placeholder:text-gray-400' : 'placeholder:italic'
        }`}
        rows={5}
        placeholder={placeholder}
        value={fieldValue ? String(fieldValue) : ''}
        onChange={(evt) => {
          updateFieldValueMap(field.id, evt.target.value)
        }}
        onFocus={(evt) => {
          scrollIntoView(evt.target)
        }}
      />
      {validationErrors[`field_${field.id}`] && (
        <div className="mt-2 text-sm text-red-600" tabIndex={-1}>
          {validationErrors[`field_${field.id}`]}
        </div>
      )}
    </div>
  )
}

const TextListItem = ({ field, updateFieldValueMap, validationErrors, fieldValue }: FormFieldElement) => {
  const [tempTextValue, setTempTextValue] = useState<string>(fieldValue ? String(fieldValue) : '')

  useEffect(() => {
    if (fieldValue !== undefined) {
      setTempTextValue(String(fieldValue))
    }
  }, [fieldValue])

  useEffect(() => {
    updateFieldValueMap(field.id, tempTextValue)
  }, [tempTextValue])

  return (
    <div id={`review-input-${field.id}`} className="mb-6">
      <label className="block text-sm font-semibold text-graphite-grey mb-2">{field.label}</label>
      <input
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white"
        onChange={(evt) => {
          setTempTextValue(evt.target.value)
        }}
        onFocus={(evt) => {
          scrollIntoView(evt.target)
        }}
        value={tempTextValue}
      />
      {validationErrors[`field_${field.id}`] && (
        <div className="mt-2 text-sm text-red-600" tabIndex={-1}>
          {validationErrors[`field_${field.id}`]}
        </div>
      )}
    </div>
  )
}

const SelectListItem = ({
  field,
  updateFieldValueMap,
  userDetails,
  validationErrors,
  disablePrimaryUnit,
  setDisablePrimaryUnit,
  fieldValue,
}: FormFieldElement & {
  userDetails: UserDTO
  disablePrimaryUnit: boolean
  setDisablePrimaryUnit: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const { id } = useParams<{ id: string }>()
  const nonRestrictedRoles = ['RN', 'LPN/LVN', 'RT']
  const isPrimaryUnitDropdown = field.label === 'Primary Unit/Specialty'
  const isHealthcareRoleDropdown = field.label === 'Healthcare Role'
  const isWrapupDropdown = field.label === 'Scrub Color' || field.label === 'Charting Software'
  const isOptionalMonthDropdown = field.inputName === LAST_WORKED_MONTH_INPUT_NAME
  const usesEmptyDefault = isWrapupDropdown || isOptionalMonthDropdown

  const getInitialValue = () => {
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '' && fieldValue !== 'null') {
      return String(fieldValue)
    }
    if (usesEmptyDefault) {
      return ''
    }
    if (isHealthcareRoleDropdown && userDetails.position_title) {
      const userValue = userDetails.position_title.toLowerCase().trim()
      const found = field.choices?.find((c) => c.value?.toLowerCase().trim() === userValue)
      if (found) return found.value
      return field.choices?.[0]?.value || ''
    }
    if (isPrimaryUnitDropdown && userDetails.nursing_specialization) {
      const userValue = userDetails.nursing_specialization.toLowerCase().trim()
      const found = field.choices?.find((c) => c.value?.toLowerCase().trim() === userValue)
      if (found) return found.value
      return field.choices?.[0]?.value || ''
    }
    if (id && (field.inputName === 'a' || field.inputName === 'h')) {
      return id
    }
    return field.choices?.[0]?.value || ''
  }

  const [selectedValue, setSelectedValue] = useState(getInitialValue)

  useEffect(() => {
    if (fieldValue !== undefined && fieldValue !== null && fieldValue !== '' && fieldValue !== 'null') {
      setSelectedValue(String(fieldValue))
    }
  }, [fieldValue])

  useEffect(() => {
    if (!isPrimaryUnitDropdown || !disablePrimaryUnit) {
      if (usesEmptyDefault && selectedValue === '') {
        updateFieldValueMap(field.id, null)
      } else {
        updateFieldValueMap(field.id, selectedValue)
      }
    } else {
      updateFieldValueMap(field.id, null)
    }
  }, [selectedValue, disablePrimaryUnit])

  useEffect(() => {
    if (isHealthcareRoleDropdown) {
      const shouldDisable = !nonRestrictedRoles.includes(selectedValue)
      setDisablePrimaryUnit?.(shouldDisable)
    }
  }, [selectedValue])

  if (isPrimaryUnitDropdown && disablePrimaryUnit) {
    return null
  }

  const choices = isOptionalMonthDropdown
    ? field.choices
    : field.choices?.sort((a, b) => a.label.localeCompare(b.label))

  return (
    <div
      id={`review-input-${field.id}`}
      className={`mb-6 ${id && (field.inputName === 'a' || field.inputName === 'h') ? 'hidden' : ''}`}
    >
      <label className="block text-sm font-semibold text-graphite-grey mb-2">{field.label}</label>
      <select
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white"
        value={selectedValue || ''}
        onChange={(evt) => setSelectedValue(evt.target.value)}
      >
        {isWrapupDropdown && <option value="">-</option>}
        {isOptionalMonthDropdown && (
          <option value="" disabled>
            Select month
          </option>
        )}
        {choices?.map((choice) => (
          <option key={choice.value} value={choice.value}>
            {choice.label}
          </option>
        ))}
      </select>
      {validationErrors[`field_${field.id}`] && (
        <div className="mt-2 text-sm text-red-600" tabIndex={-1}>
          {validationErrors[`field_${field.id}`]}
        </div>
      )}
    </div>
  )
}

const NumberListItem = ({ field, updateFieldValueMap, validationErrors, fieldValue }: FormFieldElement) => {
  return (
    <div id={`review-input-${field.id}`} className="mb-6">
      <label className="block text-sm font-semibold text-graphite-grey mb-2">{field.label}</label>
      <input
        className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-royal-indigo focus:border-transparent bg-white"
        type="number"
        value={fieldValue ? String(fieldValue) : ''}
        onChange={(evt) => {
          updateFieldValueMap(field.id, evt.target.value)
        }}
      />
      {validationErrors[`field_${field.id}`] && (
        <div className="mt-2 text-sm text-red-600" tabIndex={-1}>
          {validationErrors[`field_${field.id}`]}
        </div>
      )}
    </div>
  )
}

const NodeListItem = (
  field: FormFieldDTO,
  updateFieldValueMap: UpdateFieldValueMap,
  userDetails: UserDTO,
  validationErrors: ValidationErrors,
  disablePrimaryUnit: boolean,
  setDisablePrimaryUnit: React.Dispatch<React.SetStateAction<boolean>>,
  options?: { compactLabel?: boolean; fieldValue?: unknown; isRatingPage?: boolean; isWrapUp?: boolean },
): JSX.Element => {
  const { compactLabel, fieldValue, isRatingPage, isWrapUp } = options ?? {}
  const commonProps = {
    field,
    fieldValue,
    key: field.id,
    updateFieldValueMap,
    validationErrors,
  }

  switch (field.type) {
    case FormFieldTypes.section:
      return <SectionListItem field={field} compact={isRatingPage} />
    case FormFieldTypes.number:
      return <NumberListItem {...commonProps} />
    case FormFieldTypes.select:
      return (
        <SelectListItem
          {...commonProps}
          userDetails={userDetails}
          disablePrimaryUnit={disablePrimaryUnit}
          setDisablePrimaryUnit={setDisablePrimaryUnit}
        />
      )
    case FormFieldTypes.text:
      return <TextListItem {...commonProps} />
    case FormFieldTypes.radio:
      if (!field.isRating) {
        return (
          <ReviewYesNoField
            key={field.id}
            field={field}
            fieldValue={fieldValue}
            onChange={updateFieldValueMap}
            validationError={validationErrors[`field_${field.id}`]}
            compactLabel={compactLabel}
          />
        )
      }
      return (
        <ReviewRatingField
          key={field.id}
          field={field}
          fieldValue={fieldValue}
          onChange={updateFieldValueMap}
          validationError={validationErrors[`field_${field.id}`]}
        />
      )
    case FormFieldTypes.textarea:
      return <TextAreaListItem {...commonProps} isWrapUp={isWrapUp} />
    default:
      return <div className="text-red-500">Error: did you forget to handle this type?</div>
  }
}

const EditReview = (): JSX.Element => {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth0()
  const canWriteReview = useCanPerformVerifiedAction()
  const deferWriteGate = useShouldDeferWriteGate()
  const { id } = useParams<{ id: string }>()
  const [userDetails, setUserDetails] = useState<UserDTO>()
  const [submission, setSubmission] = useState<FormSubmissionDTO>()
  const [pages, setPages] = useState<FormPageDTO[]>([])
  const [formValid, setFormValid] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [reviewPageIndex, setReviewPageIndex] = useState<number>(0)
  const [generalErrorMessage, setGeneralErrorMessage] = useState('')
  const [submissionGeneralErrorMessage, setSubmissionGeneralErrorMessage] = useState('')
  const [validationErrors, setValidationErrors] = useState<ValidationErrors>({})
  const [fieldValues, setFieldValues] = useState<FieldValues>({})
  const [disablePrimaryUnit, setDisablePrimaryUnit] = useState(false)
  const [entityName, setEntityName] = useState<string>('')
  const [reviewType, setReviewType] = useState<ReviewEntityType>('hospital')
  const history = useHistory()
  const location = useLocation()
  const { showErrorToast, showSuccessToast } = useAppToast()
  const formService = new FormService()

  useEffect(() => {
    if (!isAuthenticated) {
      history.replace(`/login/redirect/${encodeURIComponent(location.pathname)}`)
      return
    }
    if (isAuthenticated && user) {
      setUserDetails(UserService.formatUser(user))
    }
  }, [isAuthenticated, user, history, location.pathname])

  useEffect(() => {
    if (!canWriteReview || !userDetails || !id) return

    setIsLoading(true)
    setGeneralErrorMessage('')

    // Load the submission first
    formService
      .getMyReviews()
      .then((reviews) => {
        const review = reviews.find((r) => r.id === Number(id))
        if (!review) {
          setGeneralErrorMessage('Review not found or you do not have permission to edit it.')
          setIsLoading(false)
          return null
        }
        setSubmission(review)
        if (review.form?.id) {
          const mappedType = formIdToReviewType(review.form.id)
          if (mappedType === 'agency' || mappedType === 'hospital') {
            setReviewType(mappedType)
          }
        }

        // Load the form
        if (review.form) {
          return formService
            .get({
              id: review.form.id,
              relations: [FormRelations.pages, FormRelations.pagesFields],
            })
            .then((form) => {
              return { form, review }
            })
        }
        throw new Error('Form not found in submission')
      })
      .then((result) => {
        if (!result) return
        const { form, review } = result

        // Check if pages have fields
        if (!form.pages || form.pages.length === 0) {
          setGeneralErrorMessage('No form pages found. Please contact support.')
          setIsLoading(false)
          return
        }

        const pagesWithFields = form.pages.filter((page) => page.fields && page.fields.length > 0)
        if (pagesWithFields.length === 0) {
          setGeneralErrorMessage('No form fields found. The form may not be configured correctly.')
          setIsLoading(false)
          return
        }

        // Pre-populate field values from submission answers
        const initialFieldValues: FieldValues = {}
        if (review.answers) {
          review.answers.forEach((answer) => {
            if (answer.field?.id) {
              initialFieldValues[answer.field.id] = answer.value
            }
          })
        }

        // Set default values for fields that don't have answers
        form.pages?.forEach((page) => {
          page.fields?.forEach((field) => {
            if (field.defaultValue && !initialFieldValues[field.id]) {
              let fieldDefault = ''
              if (field.defaultValue === '{user:position_title}') {
                fieldDefault = userDetails.position_title || ''
              } else if (field.defaultValue === '{user:nursing_specialization}') {
                fieldDefault = userDetails.nursing_specialization || ''
              } else {
                fieldDefault = field.defaultValue
              }
              initialFieldValues[field.id] = fieldDefault
            }
          })
        })

        setFieldValues(initialFieldValues)

        // Filter visible fields
        const pagesWithVisibleFields = form.pages?.map((page) => {
          return {
            ...page,
            fields: page.fields?.filter((field) => field.visible === true),
          }
        })

        // Handle hospital/agency fields
        const promises: Promise<void>[] = []
        pagesWithVisibleFields?.forEach((page) => {
          page.fields?.forEach((field) => {
            if (field.inputName === 'h' || field.inputName === 'a') {
              promises.push(
                new Promise((resolve, reject) => {
                  const fieldValue = initialFieldValues[field.id]
                  if (fieldValue) {
                    const entityId = Number(fieldValue)
                    if (field.inputName === 'h') {
                      const hospitalService = new HospitalService()
                      hospitalService
                        .searchOne({ id: entityId })
                        .then((hospitalResult) => {
                          field.choices = [{ label: hospitalResult.name, value: String(hospitalResult.id) }]
                          setEntityName(hospitalResult.name)
                          resolve()
                        })
                        .catch(reject)
                    } else if (field.inputName === 'a') {
                      const agencyService = new AgencyService()
                      agencyService
                        .searchOne({ id: entityId })
                        .then((agencyResult) => {
                          field.choices = [{ label: agencyResult.name, value: String(agencyResult.id) }]
                          setEntityName(agencyResult.name)
                          resolve()
                        })
                        .catch(reject)
                    }
                  } else {
                    formService
                      .getField({ id: field.id, relations: [] })
                      .then((fieldResult) => {
                        field.choices = fieldResult.choices
                        resolve()
                      })
                      .catch(reject)
                  }
                }),
              )
            }
          })
        })

        Promise.all(promises)
          .then(() => {
            setPages(pagesWithVisibleFields || [])
            setIsLoading(false)
          })
          .catch((error) => {
            console.error(error)
            setGeneralErrorMessage('Sorry, there was a problem loading the form.')
            setIsLoading(false)
          })
      })
      .catch((error) => {
        console.error(error)
        setGeneralErrorMessage('Sorry, there was a problem loading your review.')
        setIsLoading(false)
      })
  }, [canWriteReview, userDetails, id])

  useEffect(() => {
    validateFields()
  }, [fieldValues, pages])

  const validateFields = () => {
    if (pages.length) {
      const validPages: Record<string, boolean> = {}
      pages.forEach((page) => {
        validPages[`page_${page.id}`] = true
        page.fields?.forEach((field) => {
          if (![FormFieldTypes.section].includes(field.type)) {
            if (typeof fieldValues[field.id] === 'undefined') {
              if (field.visible && field.isRequired) {
                validPages[`page_${page.id}`] = false
              }
            }
          }
        })
      })
      setFormValid(Object.values(validPages).every((v) => v))
    }
  }

  const updateFieldValueMap: UpdateFieldValueMap = (fieldID, value) => {
    setFieldValues((prev) => ({
      ...prev,
      [String(fieldID)]: String(value),
    }))
  }

  const handleSubmit = async () => {
    if (!submission || !userDetails) return

    setIsSubmitting(true)
    setValidationErrors({})
    setSubmissionGeneralErrorMessage('')

    // Include all fields from the form - send all answers to update
    // The backend will update existing answers and create new ones
    const answers: FormServiceAddSubmissionAnswerEvent[] = []
    pages.forEach((page) => {
      page.fields?.forEach((field) => {
        if (field.type !== FormFieldTypes.section && field.visible) {
          const fieldValue = fieldValues[field.id]
          // Send the value - include all fields (required fields must have values due to validation)
          // For optional fields, send empty string if no value
          const value = fieldValue !== undefined && fieldValue !== null ? String(fieldValue) : ''
          answers.push({
            formFieldId: field.id,
            value: value,
          })
        }
      })
    })

    try {
      await formService.updateSubmission(submission.id, {
        answers,
        email: userDetails.email,
      })

      // Success - Navigate to the detail page for the hospital or agency
      const detailPath = getEntityDetailPathFromSubmission(submission)
      if (!detailPath) {
        showErrorToast('Could not open the hospital or agency page for this review.')
        setIsSubmitting(false)
        return
      }

      showSuccessToast('Review saved successfully.')
      await waitForReviewSuccessNavDelay()
      history.push(detailPath, { scrollToReviewId: submission.id })
    } catch (error) {
      console.error(error)
      const errorWithDetails = (error as FormSubmissionError)?.data
      if (errorWithDetails?.details?.length) {
        const validationErrors: ValidationErrors = errorWithDetails.details.reduce(
          (acc: ValidationErrors, errorDetail) => {
            acc[`field_${errorDetail.fieldId}`] = errorDetail.error
            return acc
          },
          {},
        )
        setValidationErrors(validationErrors)
        setSubmissionGeneralErrorMessage(
          errorWithDetails?.message || 'Sorry, there was a problem updating your review.',
        )
      } else {
        setSubmissionGeneralErrorMessage('Sorry, there was a problem updating your review.')
      }
      setIsSubmitting(false)
    }
  }

  const canStartReview = pages[0] ? isGetStartedPageComplete(pages[0], fieldValues) : true

  return (
    <div id="page-root" className="h-screen overflow-auto relative bg-white">
      <TopLogo />

      {!isAuthLoading && isAuthenticated && !canWriteReview && !deferWriteGate && (
        <AuthPromptModal returnTo={location.pathname} mode="verify-email" />
      )}

      {canWriteReview && userDetails && (
        <div className="px-4 max-w-4xl mx-auto py-8 relative">
          <ReviewStepHeader
            compact={reviewPageIndex > 0}
            entityName={entityName}
            reviewType={reviewType}
            subtitle="Update your review information"
            title="Edit Review"
          />

          {reviewPageIndex > 0 && pages.length > 0 && !isSubmitting && (
            <ReviewProgressBar currentStep={reviewPageIndex + 1} totalSteps={pages.length} />
          )}

          {generalErrorMessage.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-center">
              <span dangerouslySetInnerHTML={{ __html: generalErrorMessage }}></span>
            </div>
          )}

          {(isLoading || isSubmitting) && !(generalErrorMessage.length > 0) && (
            <div className="flex min-h-[400px] flex-col items-center justify-center py-20">
              <Spinner size="lg" />
              {isSubmitting && <p className="mt-4 text-sm font-medium text-gray-600">Submitting your review…</p>}
            </div>
          )}

          {!isLoading && !(generalErrorMessage.length > 0) && (
            <div className={`${isSubmitting ? 'hidden' : ''}`}>
              {/* Getting Started Page */}
              {pages?.[0] && (
                <>
                  <div
                    className={`review-getting-started-page bg-white rounded-2xl shadow-lg p-6 md:p-8 ${reviewPageIndex === 0 ? '' : 'hidden'}`}
                  >
                    {pages[0].fields?.some((field) => field.type !== 'section') ? (
                      <>
                        <ReviewGetStartedIntro reviewType={reviewType} showRoleGuidance />
                        <div className="space-y-5">
                          {pages[0].fields
                            ?.filter((field) => field.type !== 'section')
                            .map((field) =>
                              NodeListItem(
                                field,
                                updateFieldValueMap,
                                userDetails,
                                validationErrors,
                                disablePrimaryUnit,
                                setDisablePrimaryUnit,
                                { compactLabel: true, fieldValue: fieldValues[field.id] },
                              ),
                            )}
                        </div>
                      </>
                    ) : (
                      <ReviewGetStartedIntro reviewType={reviewType} />
                    )}

                    <ReviewGetStartedAssurances
                      reviewType={reviewType}
                      showRoleAssurance={Boolean(
                        pages[0].fields?.some((field) => field.visible && field.type === 'select'),
                      )}
                    />

                    <div className="mt-8 flex justify-center">
                      <button
                        type="button"
                        disabled={!canStartReview}
                        className={`w-full max-w-md rounded-xl px-8 py-3.5 text-lg font-semibold text-white shadow-md transition-all ${
                          canStartReview
                            ? 'bg-orange hover:bg-opacity-90'
                            : 'cursor-not-allowed bg-orange/40 text-white/80'
                        }`}
                        onClick={() => {
                          if (!canStartReview) return
                          setReviewPageIndex(reviewPageIndex + 1)
                        }}
                      >
                        Continue
                      </button>
                    </div>
                  </div>
                  {reviewPageIndex === 0 && <ReviewGetStartedDisclaimer />}
                </>
              )}

              {/* Rating Pages */}
              {pages.map((page, pageIndex) => {
                if (pageIndex !== 0 && pageIndex !== pages.length - 1 && page.fields?.length) {
                  const canProceed = isReviewQuestionPageComplete(page, fieldValues)
                  return (
                    <div
                      key={page.id}
                      className={`review-ratings-page bg-white rounded-2xl p-6 md:p-8 ${
                        reviewPageIndex === pageIndex ? '' : 'hidden'
                      }`}
                    >
                      <div className="space-y-4">
                        {page.fields.map((field) =>
                          NodeListItem(
                            field,
                            updateFieldValueMap,
                            userDetails,
                            validationErrors,
                            disablePrimaryUnit,
                            setDisablePrimaryUnit,
                            { fieldValue: fieldValues[field.id], isRatingPage: true },
                          ),
                        )}
                      </div>
                      <div className="mt-10 flex gap-3">
                        <button
                          type="button"
                          className="flex-1 rounded-xl bg-gray-100 py-3.5 font-semibold text-graphite-grey transition-all hover:bg-gray-200"
                          onClick={() => setReviewPageIndex(reviewPageIndex - 1)}
                        >
                          Previous
                        </button>
                        <button
                          type="button"
                          disabled={!canProceed}
                          className={`flex-1 rounded-xl py-3.5 font-semibold transition-all ${
                            canProceed
                              ? 'bg-orange text-white hover:bg-orange/90'
                              : 'cursor-not-allowed bg-orange/40 text-white/80'
                          }`}
                          onClick={() => {
                            if (!canProceed) return
                            setReviewPageIndex(reviewPageIndex + 1)
                          }}
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )
                }
              })}

              {/* Wrapup Page */}
              {pages[pages.length - 1] && (
                <div
                  className={`review-wrapup-page bg-white rounded-2xl p-6 md:p-8 ${
                    reviewPageIndex === pages.length - 1 ? '' : 'hidden'
                  }`}
                >
                  {pages[pages.length - 1].fields
                    ?.filter((field) => isWrapUpVisibleField(field) && isWouldWorkAgainField(field))
                    .map((field) =>
                      NodeListItem(
                        field,
                        updateFieldValueMap,
                        userDetails,
                        validationErrors,
                        disablePrimaryUnit,
                        setDisablePrimaryUnit,
                        { fieldValue: fieldValues[field.id], isWrapUp: true },
                      ),
                    )}
                  <ReviewWrapUpIntro />
                  <div className="space-y-6">
                    {pages[pages.length - 1].fields
                      ?.filter((field) => isWrapUpVisibleField(field) && !isWouldWorkAgainField(field))
                      .map((field) =>
                        NodeListItem(
                          field,
                          updateFieldValueMap,
                          userDetails,
                          validationErrors,
                          disablePrimaryUnit,
                          setDisablePrimaryUnit,
                          { fieldValue: fieldValues[field.id], isWrapUp: true },
                        ),
                      )}
                  </div>
                  {submissionGeneralErrorMessage.length > 0 && (
                    <div
                      className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 text-center"
                      dangerouslySetInnerHTML={{ __html: submissionGeneralErrorMessage }}
                    ></div>
                  )}
                  {!formValid && (
                    <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-800 text-center">
                      Please complete all required fields before submitting.
                    </div>
                  )}
                  {formValid && (
                    <div className="mt-8 flex justify-center gap-4">
                      <button
                        type="button"
                        disabled={isSubmitting}
                        className={`rounded-lg bg-gray-200 px-8 py-3 font-semibold text-graphite-grey transition-all ${
                          isSubmitting ? 'cursor-not-allowed opacity-50' : 'hover:bg-gray-300'
                        }`}
                        onClick={() => {
                          if (isSubmitting) return
                          setReviewPageIndex(reviewPageIndex - 1)
                        }}
                      >
                        Previous
                      </button>
                      <button
                        disabled={!formValid || isSubmitting}
                        className={`font-semibold rounded-lg py-3 px-12 text-lg transition-all shadow-md ${
                          formValid && !isSubmitting
                            ? 'bg-orange text-white hover:bg-opacity-90'
                            : 'cursor-not-allowed bg-orange/40 text-white/80'
                        }`}
                        onClick={handleSubmit}
                      >
                        Save Changes
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default EditReview
